import {
  ContactRequest,
  ContactRequestContext,
  ProductApiIdentifier,
  RequestContext,
  RequestTypes
} from 'types/ContactRequests';
import {
  createBrandPortalContactRequest,
  createGenericContactRequest,
  createSupplierPortalContactRequestV2
} from 'api/services/RequestContacts';
import { createContactRequest } from 'api/services/ProjectsService';
import { toast } from 'react-toastify';
import { translateReverse } from 'api/types/ReferenceData';
import { Countries } from 'api/types/countries';

export const submitRequestByContext = (
  context: RequestContext,
  body: Partial<ContactRequest> | SupplierPortalRequestBody,
  requestType: RequestTypes,
  setModal: Function,
  setIsSubmitting: Function,
  project?:
    | {
        description: string;
        title: string;
        key: string;
      }
    | undefined
) => {
  switch (context.type) {
    case ContactRequestContext.COVALO:
      createGenericContactRequest(
        body as Partial<ContactRequest>,
        () => {
          toast.success('Request created!');
          setModal(false);
          setIsSubmitting(false);
        },
        (error: any) => {
          setIsSubmitting(false);
          toast.error(error?.response?.data?.detail ?? error.message);
        }
      );
      break;
    case ContactRequestContext.BRAND_PORTAL: {
      const typedBody = body as Partial<ContactRequest>;
      context.portalKey &&
        createBrandPortalContactRequest(
          {
            ...typedBody,
            request: {
              ...typedBody.request,
              customContacts: sanitizeCustomContactsForRequest(
                typedBody.request?.customContacts
              )
            }
          },
          () => {
            toast.success('Request created!');
            setModal(false);
            setIsSubmitting(false);
          },
          context.portalKey,
          (error: any) => {
            setIsSubmitting(false);
            toast.error(error?.response?.data?.detail ?? error.message);
          }
        );
      break;
    }
    case ContactRequestContext.SUPPLIER_PORTAL: {
      const requestBody = supplierPortalRequestBody(
        body as SupplierPortalRequestBody
      );
      if (context.portalKey && requestBody) {
        createSupplierPortalContactRequestV2(
          requestBody,
          context.portalKey,
          () => {
            toast.success('Request created!');
            setModal(false);
            setIsSubmitting(false);
          },
          (error: any) => {
            setIsSubmitting(false);
            toast.error(error?.response?.data?.detail ?? error.message);
          }
        );
      }
      break;
    }
    case ContactRequestContext.PROJECT: {
      const bodyInstance: Partial<ContactRequest> =
        body as Partial<ContactRequest>;
      bodyInstance.project = project;
      if (bodyInstance.project.include === false) {
        bodyInstance.project.description = '';
      }
      createContactRequest(
        bodyInstance,
        bodyInstance.project?.company?.key,
        project?.key,
        () => {
          toast.success('Request created!');
          setModal(requestType);
          setIsSubmitting(false);
        },
        (error: any) => {
          toast.error(error?.response?.data?.detail ?? error.message);
          setIsSubmitting(false);
        }
      );
      break;
    }
    default:
      break;
  }
};

const sanitizeCustomContactsForRequest = (customContacts?: {
  to: string[];
  cc: string[];
}) => {
  if (
    customContacts &&
    customContacts.to.length === 0 &&
    customContacts.cc.length === 0
  ) {
    return undefined;
  }
  return customContacts;
};

interface SupplierPortalRequestBody {
  type: RequestTypes;
  request: {
    message: string;
    documentTypes: string[];
    otherDocumentType: string;
    phoneNumber: string;
    acknowledge: boolean;
    quantityRequired: string;
    estimateUsage: string;
    neededBy: string;
    quantityRequiredUnits: string;
    estimatedUsageUnits: string;
    shippingAddress: {
      city: string;
      country: string;
    };
  };
  products: ProductApiIdentifier[];
}

const supplierPortalRequestBody = (body: SupplierPortalRequestBody) => {
  const common = {
    type: body.type,
    message: body.request?.message
  };

  const products: ProductApiIdentifier[] = Array.isArray(body.products)
    ? body.products
    : [body.products];
  switch (body.type) {
    case RequestTypes.DOCUMENT: {
      const updatedBody = {
        type: body.type,
        message: body.request?.message,
        requests: products.map(product => {
          return {
            product_id: {
              company: product.company.key,
              segment: product.segment,
              product: product.key
            },
            document_types: body.request?.documentTypes,
            other_document_type: body.request?.otherDocumentType
          };
        })
      };
      return updatedBody;
    }
    case RequestTypes.QUOTE: {
      const updatedBody = {
        ...common,
        shipping_address: {
          city: body.request?.shippingAddress?.city,
          country: body.request?.shippingAddress?.country,
          country_code: translateReverse(
            body.request?.shippingAddress?.country,
            Countries
          ),
          phone_number: body.request?.phoneNumber
        },
        requests: products.map(product => {
          return {
            product_id: {
              company: product.company.key,
              segment: product.segment,
              product: product.key
            },
            quantity_required: body.request?.quantityRequired,
            estimate_usage: body.request?.estimateUsage,
            needed_by: body.request?.neededBy,
            quantity_required_units: body.request?.quantityRequiredUnits,
            estimated_usage_units: body.request?.estimatedUsageUnits
          };
        })
      };
      return updatedBody;
    }
    case RequestTypes.CONTACT: {
      const updatedBody = {
        ...common,
        acknowledge: body?.request?.acknowledge,
        requests: products.map(product => {
          return {
            product_id: {
              company: product.company.key,
              segment: product.segment,
              product: product.key
            }
          };
        })
      };
      return updatedBody;
    }
    default:
      break;
  }
};
